export const DV_PACKAGES = {
  standard: {
    key: "standard",
    name: "DV Бүртгэлийн Бэлтгэл",
    tagline: "Бид бүхнийг бэлдэнэ. Та official сайтад бэлэн мэдээллээ оруулна.",
    individualOriginalPrice: 98_000,
    individualPrice: 49_000,
    familyOriginalPrice: 138_000,
    familyPrice: 69_000,
  },
  vip: {
    key: "vip",
    name: "VIP Бүртгэл",
    tagline: "Бид бүртгэлийг танд зориулж гүйцэтгэж өгнө.",
    individualOriginalPrice: 198_000,
    individualPrice: 99_000,
    familyOriginalPrice: 278_000,
    familyPrice: 139_000,
  },
} as const;

export const DV_PROMOTION = {
  label: "50% ХЯМДРАЛ",
  pricePhrase: "50% хямдраад",
  deadline: "11-р сарын 1-нээс өмнө",
} as const;

export type DvPackageKey = keyof typeof DV_PACKAGES;
export type DvHouseholdType = "individual" | "family";
export type DvEligibilityOutcome = "qualified" | "manual_review" | "not_qualified";
export type DvEducationAnswer = "yes" | "no" | "not_sure";
export type DvWorkExperienceAnswer = "yes" | "no";

export const DV_PAYLINK_URLS: Record<DvPackageKey, Record<DvHouseholdType, string>> = {
  standard: {
    individual: "https://paylink.mn/api/s/inv_mvadvy3r4481aa",
    family: "https://paylink.mn/api/s/inv_mvadx5cu69fe2b",
  },
  vip: {
    individual: "https://paylink.mn/api/s/inv_mvadz8raf595d3",
    family: "https://paylink.mn/api/s/inv_mvae0hv7d02d21",
  },
};

export function getDvPrice(packageType: DvPackageKey, householdType: DvHouseholdType) {
  const packageInfo = DV_PACKAGES[packageType];
  return householdType === "family" ? packageInfo.familyPrice : packageInfo.individualPrice;
}

export function getDvOriginalPrice(packageType: DvPackageKey, householdType: DvHouseholdType) {
  const packageInfo = DV_PACKAGES[packageType];
  return householdType === "family" ? packageInfo.familyOriginalPrice : packageInfo.individualOriginalPrice;
}

export function getDvPaylinkUrl(packageType: DvPackageKey, householdType: DvHouseholdType) {
  return DV_PAYLINK_URLS[packageType][householdType];
}

export function getDvPaymentReference(email: string) {
  return email.trim();
}

export type DvOwnerOrderEmailInput = {
  name: string;
  phone: string;
  email: string;
  orderCode: string;
  packageType: DvPackageKey;
  householdType: DvHouseholdType;
  amount: number;
};

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, char => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[char] || char);
}

/** Builds the owner notice sent when a visitor starts the Paylink/QPay checkout flow. */
export function buildDvOwnerOrderEmail(input: DvOwnerOrderEmailInput) {
  const packageName = DV_PACKAGES[input.packageType].name;
  const householdLabel = input.householdType === "family" ? "Гэр бүл" : "Ганц бие";
  const amount = `${input.amount.toLocaleString("mn-MN")}₮`;
  const subjectName = input.name.replace(/[\r\n]+/g, " ").trim();
  const text = [
    "DV — Paylink/QPay төлбөрийн хуудас руу шилжих захиалга үүслээ.",
    `Захиалгын код: ${input.orderCode}`,
    `Нэр: ${input.name}`,
    `Утас: ${input.phone}`,
    `Имэйл: ${input.email}`,
    `Үйлчилгээ: ${packageName} (${householdLabel})`,
    `Дүн: ${amount}`,
    "Төлөв: Төлбөр баталгаажаагүй — Paylink/QPay хуудас руу үргэлжлэх шатанд байна.",
  ].join("\n");
  const row = (label: string, value: string) => `<tr><td style="padding:10px 0;color:#64748b;width:130px"><b>${escapeHtml(label)}</b></td><td style="padding:10px 0;color:#0B1F3A">${escapeHtml(value)}</td></tr>`;

  return {
    subject: `DV Paylink/QPay захиалга — ${subjectName}`,
    text,
    html: `<div style="font-family:Arial,sans-serif;max-width:620px;margin:0 auto;color:#0B1F3A"><div style="background:#0B1F3A;color:#fff;padding:24px;border-radius:16px 16px 0 0"><p style="margin:0 0 5px;font-size:12px;letter-spacing:1px;color:#D4A94C;font-weight:700">UNITED DREAM MONGOLIA · DV</p><h2 style="margin:0;font-size:22px">Paylink/QPay захиалга үүслээ</h2></div><div style="border:1px solid #E2E8F0;border-top:0;padding:24px;border-radius:0 0 16px 16px"><p style="margin:0 0 16px;color:#475569;line-height:1.6">Хэрэглэгч төлбөрийн хуудас руу үргэлжлүүлэх товч дарсан байна. Энэ нь төлбөр баталгаажсан гэсэн үг биш.</p><table style="width:100%;border-collapse:collapse">${row("Захиалгын код", input.orderCode)}${row("Нэр", input.name)}${row("Утас", input.phone)}${row("Имэйл", input.email)}${row("Үйлчилгээ", `${packageName} — ${householdLabel}`)}${row("Дүн", amount)}</table><p style="margin:20px 0 0;padding:12px 14px;border-radius:10px;background:#FFF9E9;color:#74520B;font-size:13px;line-height:1.5"><b>Төлөв:</b> Төлбөр баталгаажаагүй. Paylink/QPay дээрх гүйлгээг админаар нягтална.</p></div></div>`,
  };
}

export type DvCustomerEmailInput = {
  name: string;
  orderCode: string;
  packageType: DvPackageKey;
  householdType: DvHouseholdType;
  amount: number;
  paymentStatus: "payment_pending" | "paid" | "rejected" | "cancelled";
  accessUrl: string;
};

export type DvPublicIntakeInvitationEmailInput = {
  name: string;
  intakeUrl: string;
};

/** Builds the public multi-step DV intake invitation sent to existing DV leads. */
export function buildDvPublicIntakeInvitationEmail(input: DvPublicIntakeInvitationEmailInput) {
  const safeName = input.name.replace(/[\r\n]+/g, " ").trim();
  const text = [
    `Сайн байна уу, ${safeName}.`,
    "",
    "Бидэнд Ногоон карт / DV Lottery-ийн талаарх мэдээллээ илгээсэнд баярлалаа.",
    "Та доорх олон алхамт анкетын холбоосоор орж дэлгэрэнгүй мэдээллээ илгээнэ үү.",
    "",
    `DV олон алхамт анкет: ${input.intakeUrl}`,
    "",
    "Холбогдох: 8080-6633, 9092-6633",
    "United Dream Mongolia",
  ].join("\n");

  return {
    subject: "United Dream Mongolia — DV Lottery олон алхамт анкет",
    text,
    html: `<div style="font-family:Arial,sans-serif;max-width:620px;margin:0 auto;color:#0B1F3A"><div style="background:#0B1F3A;color:#fff;padding:24px;border-radius:16px 16px 0 0"><p style="margin:0 0 5px;font-size:12px;letter-spacing:1px;color:#D4A94C;font-weight:700">UNITED DREAM MONGOLIA · DV</p><h2 style="margin:0;font-size:22px">DV Lottery олон алхамт анкет</h2></div><div style="border:1px solid #E2E8F0;border-top:0;padding:24px;border-radius:0 0 16px 16px"><p style="margin:0;color:#475569;line-height:1.7">Сайн байна уу, <b>${escapeHtml(safeName)}</b>.</p><p style="margin:16px 0 0;color:#475569;line-height:1.7">Бидэнд Ногоон карт / DV Lottery-ийн талаарх мэдээллээ илгээсэнд баярлалаа. Та доорх олон алхамт анкетын холбоосоор орж дэлгэрэнгүй мэдээллээ илгээнэ үү.</p><a href="${escapeHtml(input.intakeUrl)}" style="display:inline-block;margin-top:22px;background:#0E5D92;color:#fff;text-decoration:none;padding:14px 20px;border-radius:10px;font-weight:700;font-size:15px">DV олон алхамт анкет бөглөх</a><p style="margin:18px 0 0;font-size:13px;color:#475569">Холбогдох: 8080-6633, 9092-6633<br />United Dream Mongolia</p></div></div>`,
  };
}

/** Builds the one-to-one DV follow-up email an administrator can send to a customer. */
export function buildDvCustomerEmail(input: DvCustomerEmailInput) {
  const packageName = DV_PACKAGES[input.packageType].name;
  const householdLabel = input.householdType === "family" ? "Гэр бүлийн үйлчилгээ" : "Ганц бие үйлчилгээ";
  const amount = `${input.amount.toLocaleString("mn-MN")}₮`;
  const paid = input.paymentStatus === "paid";
  const rejected = input.paymentStatus === "rejected" || input.paymentStatus === "cancelled";
  const statusText = paid
    ? "Таны төлбөр баталгаажсан байна. Доорх хувийн холбоосоор орж DV бүртгэлийн бэлтгэлээ үргэлжлүүлнэ үү."
    : rejected
      ? "Төлбөрийн төлөв дээр нэмэлт нягтлал шаардлагатай байна. Асуух зүйл байвал манай төвтэй холбогдоно уу."
      : "Таны захиалга бүртгэгдсэн байна. Төлбөрийн гүйлгээг манай админ шалгаж баталгаажуулна."
  const subjectName = input.name.replace(/[\r\n]+/g, " ").trim();
  const text = [
    `Сайн байна уу, ${input.name}.`,
    "",
    "United Dream Mongolia-аас таны DV Lottery үйлчилгээний захиалгын мэдээллийг илгээж байна.",
    `Захиалгын код: ${input.orderCode}`,
    `Үйлчилгээ: ${packageName} — ${householdLabel}`,
    `Төлөх дүн: ${amount}`,
    "",
    statusText,
    `Таны хувийн DV холбоос: ${input.accessUrl}`,
    "",
    "Холбогдох: 8080-6633, 9092-6633",
    "United Dream Mongolia",
  ].join("\n");
  const row = (label: string, value: string) => `<tr><td style="padding:10px 0;color:#64748b;width:130px"><b>${escapeHtml(label)}</b></td><td style="padding:10px 0;color:#0B1F3A">${escapeHtml(value)}</td></tr>`;

  return {
    subject: `United Dream Mongolia — DV захиалгын мэдээлэл (${subjectName})`,
    text,
    html: `<div style="font-family:Arial,sans-serif;max-width:620px;margin:0 auto;color:#0B1F3A"><div style="background:#0B1F3A;color:#fff;padding:24px;border-radius:16px 16px 0 0"><p style="margin:0 0 5px;font-size:12px;letter-spacing:1px;color:#D4A94C;font-weight:700">UNITED DREAM MONGOLIA · DV</p><h2 style="margin:0;font-size:22px">Таны DV захиалгын мэдээлэл</h2></div><div style="border:1px solid #E2E8F0;border-top:0;padding:24px;border-radius:0 0 16px 16px"><p style="margin:0 0 16px;color:#475569;line-height:1.6">Сайн байна уу, <b>${escapeHtml(input.name)}</b>. Таны DV Lottery үйлчилгээний захиалгын мэдээлэл доор байна.</p><table style="width:100%;border-collapse:collapse">${row("Захиалгын код", input.orderCode)}${row("Үйлчилгээ", `${packageName} — ${householdLabel}`)}${row("Төлөх дүн", amount)}</table><p style="margin:20px 0 0;padding:12px 14px;border-radius:10px;background:${paid ? "#ECFDF5" : rejected ? "#FEF2F2" : "#FFF9E9"};color:${paid ? "#166534" : rejected ? "#991B1B" : "#74520B"};font-size:13px;line-height:1.5">${escapeHtml(statusText)}</p><a href="${escapeHtml(input.accessUrl)}" style="display:inline-block;margin-top:20px;background:#0E5D92;color:#fff;text-decoration:none;padding:13px 18px;border-radius:10px;font-weight:700;font-size:14px">Миний DV мэдээлэл нээх</a><p style="margin:18px 0 0;font-size:12px;line-height:1.5;color:#64748B">Энэ холбоос нь зөвхөн таны захиалгад хамаарна. Бусадтай хуваалцахгүй байна уу.</p><p style="margin:16px 0 0;font-size:13px;color:#475569">Холбогдох: 8080-6633, 9092-6633<br />United Dream Mongolia</p></div></div>`,
  };
}

export function getEligibilityOutcome(
  educationAnswer: DvEducationAnswer,
  workExperienceAnswer?: DvWorkExperienceAnswer,
): DvEligibilityOutcome {
  if (educationAnswer === "yes") return "qualified";
  if (educationAnswer === "not_sure") return "manual_review";
  return workExperienceAnswer === "yes" ? "manual_review" : "not_qualified";
}

export const DV_PUBLIC_DISCLAIMER = "Манай байгууллага нь АНУ-ын Засгийн газар, U.S. Department of State, U.S. Embassy эсвэл Diversity Visa Program-ийн албан ёсны байгууллага биш. Үйлчилгээ нь DV Lottery-д сонгогдох магадлалыг нэмэгдүүлэхгүй.";

export const DV_OFFICIAL_SITE = "https://dvprogram.state.gov/";
